import React from 'react';
import { Clock, Globe, Info } from 'lucide-react';

/*
 * Read-only Termin-Anzeige fuer das Admin-Tool.
 * Bildet die "Termine"-Sektion der Frontend-Seite aktivitaet-kontrolle/:id
 * (showAll-Modus, also ohne Datums-Filter) inhaltlich nach.
 */

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAYS_DE = {
  monday: 'Montag', tuesday: 'Dienstag', wednesday: 'Mittwoch',
  thursday: 'Donnerstag', friday: 'Freitag', saturday: 'Samstag', sunday: 'Sonntag',
};
const RECURRENCE_LABELS = {
  daily: 'Täglich',
  weekly: 'Wöchentlich',
  biweekly: 'Jeden 2. ',
  triweekly: 'Jeden 3. ',
  fourweekly: 'Jeden 4. ',
  monthly_first: 'Jeden 1. ',
  monthly_second: 'Jeden 2. ',
  monthly_third: 'Jeden 3.',
  monthly_fourth: 'Jeden 4. ',
  monthly_last: 'Jeden letzten',
};

const formatDateLabel = (date) =>
  new Date(date).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });

const periodStyle = { margin: 'var(--spacing-2) 0 0', fontSize: 'var(--font-size-sm)', color: '#6c757d' };

// Renders one or more validity/accessibility date ranges. Prefers the
// date_ranges list and falls back to a single from/until pair (legacy data).
const ValidityPeriods = ({ ranges, fallbackFrom, fallbackUntil, freelyAccessible }) => {
  const list = ranges && ranges.length > 0
    ? ranges.filter((r) => r.from || r.until)
    : ((fallbackFrom || fallbackUntil) ? [{ from: fallbackFrom, until: fallbackUntil }] : []);
  if (list.length === 0) return null;

  const prefix = freelyAccessible ? 'Aktivität ist frei zugänglich' : 'Die Termine gelten';
  const fmt = (d) => new Date(d).toLocaleDateString('de-DE');
  const rangeText = (r) => {
    if (r.from && r.until) return `${fmt(r.from)} – ${fmt(r.until)}`;
    if (r.from) return `ab ${fmt(r.from)}`;
    return `bis ${fmt(r.until)}`;
  };

  if (list.length === 1) {
    return <p style={periodStyle}>{`${prefix} von ${rangeText(list[0])}.`}</p>;
  }
  return (
    <div style={periodStyle}>
      <p style={{ margin: 0 }}>{`${prefix} in folgenden Zeiträumen:`}</p>
      <ul style={{ margin: '2px 0 0', paddingLeft: '1.2em' }}>
        {list.map((r, i) => <li key={i}>{rangeText(r)}</li>)}
      </ul>
    </div>
  );
};

const TimeSlots = ({ slots }) => {
  if (!slots || slots.length === 0) return null;
  return slots.map((tp, i) => (
    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#333' }}>
      <Clock size={16} color="#1e3a5f" />
      <span style={{ fontWeight: 600 }}>
        {tp.start_time}{tp.end_time && ` – ${tp.end_time}`} Uhr
      </span>
    </div>
  ));
};

const collectTimes = (schedule) => {
  const times = [];
  if (schedule.start_time) times.push({ start_time: schedule.start_time, end_time: schedule.end_time });
  (schedule.additional_times || []).forEach((tp) => times.push(tp));
  return times;
};

const entryStyle = {
  display: 'flex',
  flexDirection: 'row',
  gap: '1.25rem',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 'var(--radius-sm)',
};

const EventScheduleDisplay = ({ eventType, openingHours, eventSchedule, accessibleFrom, accessibleUntil, website }) => {
  let body = null;

  // Frei zugaengliche Events
  if (eventType === 'freely_accessible') {
    body = (
      <div style={entryStyle}>
        <p style={{ fontWeight: 600, margin: 0 }}>Frei zugänglich</p>
        <ValidityPeriods
          ranges={eventSchedule?.date_ranges}
          fallbackFrom={accessibleFrom}
          fallbackUntil={accessibleUntil}
          freelyAccessible
        />
      </div>
    );
  }
  // Einzeltermin(e) / Mehrtaegiger Event
  else if ((eventType === 'single_event' || eventType === 'multi_day') && eventSchedule) {
    const eventDates = eventSchedule.dates || (eventSchedule.date
      ? [{ date: eventSchedule.date, start_time: eventSchedule.start_time, end_time: eventSchedule.end_time }]
      : []);
    body = eventDates.length > 0 ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
        {eventDates.map((ed, index) => (
          <div key={index} style={entryStyle}>
            <p style={{ fontWeight: 500, margin: 0 }}>{formatDateLabel(ed.date)}</p>
            <TimeSlots slots={ed.start_time ? [{ start_time: ed.start_time, end_time: ed.end_time }, ...(ed.additional_times || [])] : (ed.additional_times || [])} />
          </div>
        ))}
      </div>
    ) : null;
  }
  // Wiederkehrende Events – Zusammenfassung der Wiederholungsregel
  else if (eventType === 'recurring_event' && eventSchedule) {
    const rec = eventSchedule.recurrence || 'daily';
    const times = collectTimes(eventSchedule);
    let label;
    if (rec === 'daily') {
      label = 'Täglich';
    } else {
      const recLabel = RECURRENCE_LABELS[rec] || rec;
      const dayLabel = eventSchedule.day_of_week ? DAYS_DE[eventSchedule.day_of_week] : null;
      label = (dayLabel ? `${recLabel} ${dayLabel}` : recLabel) + (rec.startsWith('monthly_') ? ' im Monat' : '');
    }
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
        {times.map((tp, i) => (
          <div key={i} style={entryStyle}>
            <p style={{ fontWeight: 500, margin: 0 }}>{label}</p>
            <TimeSlots slots={[{ start_time: tp.start_time, end_time: tp.end_time }]} />
          </div>
        ))}
        <ValidityPeriods
          ranges={eventSchedule.date_ranges}
          fallbackFrom={eventSchedule.valid_from}
          fallbackUntil={eventSchedule.valid_until}
        />
      </div>
    );
  }
  // Regulaere Oeffnungszeiten
  else if (eventType === 'regular_hours' && openingHours) {
    const days = DAYS_ORDER.filter((day) => openingHours.some((h) => h.day === day));
    body = days.length > 0 ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
        {days.map((day) => {
          const hours = openingHours.find((h) => h.day === day);
          return (
            <div key={day} style={entryStyle}>
              <p style={{ fontWeight: 500, margin: 0 }}>{DAYS_DE[day]}</p>
              {hours && hours.opens && (
                <TimeSlots slots={[{ start_time: hours.opens, end_time: hours.closes || null }]} />
              )}
            </div>
          );
        })}
        <ValidityPeriods
          ranges={eventSchedule?.date_ranges}
          fallbackFrom={eventSchedule?.valid_from || eventSchedule?.validFrom}
          fallbackUntil={eventSchedule?.valid_until || eventSchedule?.validUntil}
        />
      </div>
    ) : null;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
      <h3 style={{ margin: 0, fontSize: '1rem' }}>Termine</h3>

      {body || (
        <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>Keine Termine verfügbar</p>
      )}

      {website && (
        <a
          href={website}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', color: '#1e3a5f', fontSize: 'var(--font-size-sm)' }}
        >
          <Globe size={16} /> Zur Webseite
        </a>
      )}

      <p style={{ display: 'flex', alignItems: 'flex-start', gap: '0.375rem', margin: 0, fontSize: '0.8rem', color: '#6c757d' }}>
        <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
        Terminangaben und Texte ohne Gewähr. Bitte überprüfen Sie die Seite der Aktivität.
      </p>
    </div>
  );
};

export default EventScheduleDisplay;
